import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';
import { FEATURE_DEFAULTS, type FeatureFlags, type FeatureKey } from '@attendance/shared/features';
import { brandingApi, type PublicBranding } from '../api/branding.api.js';
import { liftForNavActive } from '../lib/readableInk.js';
import {
  applyTerminology,
  DEFAULT_TERMS,
  localiseTerms,
  type Terms,
} from '../lib/terminology.js';

interface BrandingState {
  branding: PublicBranding | null;
  version: number;
  bootstrap: () => Promise<void>;
  refresh: () => Promise<void>;
  bumpVersion: () => void;
}

const DEFAULT_BRANDING: PublicBranding = {
  appName: 'Attendance',
  primaryColor: '#6C47FF', // Hadir indigo-violet brand (changeable in Branding settings)
  logoUrl: null,
  dateFormat: 'DD-MM-YYYY',
  timeFormat: '24h',
};

/** "#0066ff" → "0 102 255" (space-separated RGB channels for the --primary token). */
function hexToRgbChannels(hex: string): string | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

function applyBranding(branding: PublicBranding | null): void {
  const color = branding?.primaryColor ?? DEFAULT_BRANDING.primaryColor;
  const root = document.documentElement;
  // Legacy hex var for the `.brand-*` helpers in index.css.
  //
  // CAUTION — this is an INLINE property on <html>, so it outranks both `:root`
  // and `.dark`. index.css gives `--brand-primary` a light/dark split (#5323e6
  // / #c9beff, because the light violet reads 3.52:1 on the dark page), and the
  // moment this line runs that split is dead for the rest of the session: one
  // tenant hex, both themes. index.css says as much — "a tenant override
  // inherits this file's light/dark split only if it sets both" — and a store
  // cannot set both from an inline style.
  //
  // So nothing may pair a theme-flipping token against this frozen fill. The
  // logo monogram did (`text-primary-foreground`) and measured 2.73:1 in dark
  // on the live tenant colour; it now derives its ink from the fill's own
  // luminance instead (see lib/readableInk.ts). The real fix for the general
  // case is per-tenant M3 re-seeding, which would give the tenant colour a dark
  // tone of its own — still a follow-up, per the note below.
  root.style.setProperty('--brand-primary', color);
  // NOTE: --primary/--ring are now owned by the Material 3 scheme in index.css
  // (which is light/dark-aware — a single tenant hex can't express both modes).
  // Per-tenant M3 re-seeding (generating the full scheme from the brand colour)
  // is a follow-up; for now the M3 Hadir palette is authoritative.
  /* THE ONE MARK ON THE RAIL THAT SAYS WHERE YOU ARE, in the tenant's colour.
     `--nav-active` shipped as a hand-lift of the SEED brand (#5323e6 → #9d84ff),
     so a tenant who set their own colour recoloured the logo tile and left the
     rail wearing Hadir violet on somebody else's install.
     The hex cannot be written through raw: the rail is ink in both themes and a
     dark brand simply vanishes on it (#0047D6 measures 1.76:1). `liftForNavActive`
     raises only the LIGHTNESS — the hue and saturation stay the tenant's — until
     the marker clears 4.4:1 over the current row's composite, which is what the
     seed itself measures there.
     `null` is a real answer: a hue that cannot get there without washing out
     keeps the seed token, because an unreadable marker is worse than an
     off-brand one (§3.11's cousin — the mark that says where you are must say
     it). Set on <html> like `--brand-primary` above, and for the same reason
     it is safe here where it was not for `--primary`: this token has ONE value
     across both themes already, so an inline property freezes nothing. */
  const lifted = liftForNavActive(color);
  if (lifted) {
    root.style.setProperty('--nav-active', lifted.join(' '));
  } else {
    root.style.removeProperty('--nav-active');
  }
  void hexToRgbChannels;
  if (branding?.appName) {
    document.title = branding.appName;
  }
  // Push the tenant's terminology into i18n so navigation relabels immediately.
  applyTerminology(branding?.terminology?.terms);
}

export const useBrandingStore = create<BrandingState>((set, get) => ({
  branding: null,
  version: 0,
  async bootstrap() {
    const branding = await brandingApi.getPublic();
    set({ branding: branding ?? DEFAULT_BRANDING });
    applyBranding(branding ?? DEFAULT_BRANDING);
  },
  async refresh() {
    const branding = await brandingApi.getPublic();
    set({ branding: branding ?? DEFAULT_BRANDING, version: get().version + 1 });
    applyBranding(branding ?? DEFAULT_BRANDING);
  },
  bumpVersion() {
    set((s) => ({ version: s.version + 1 }));
  },
}));

export const defaultBranding = DEFAULT_BRANDING;

/**
 * Effective terminology term set from the loaded branding, or the default word
 * set before branding resolves. Use for page headings and body copy.
 */
export function useTerminology(): Terms {
  const { t } = useTranslation();
  const terms = useBrandingStore((s) => s.branding?.terminology?.terms) ?? DEFAULT_TERMS;
  // Terminology is stored in English and interpolated into copy everywhere, so
  // without this Arabic reads half-translated — "Site الرئيسي", "حضور Team".
  // A word the tenant typed themselves passes through untouched: it is their
  // brand, not a string to translate. See lib/terminology.ts.
  return useMemo(() => localiseTerms(terms, t), [terms, t]);
}

/**
 * Enabled feature flags from the loaded branding. Defaults to everything ON
 * (so legacy/pre-install renders the full app), matching the schema defaults.
 */
export function useFeatures(): FeatureFlags {
  return useBrandingStore((s) => s.branding?.features) ?? FEATURE_DEFAULTS;
}

/** Single-feature gate hook. Unknown/absent feature → enabled (fail-open). */
export function useFeatureEnabled(feature: FeatureKey | undefined): boolean {
  const features = useFeatures();
  return feature ? features[feature] !== false : true;
}
